import { env } from '@/config/env';

/**
 * Guards every merchant-supplied URL before it reaches an `src` or an `href`.
 *
 * Merchant strings are attacker-controlled from our perspective. An unvalidated `logoUrl` in
 * `<img src>` is a `javascript:` sink in older engines and a tracking beacon in all of them; an
 * unvalidated `href` is a straightforward navigation hijack on a payment page.
 *
 * The backend returns absolute HTTPS URLs, so this check is a scheme allowlist only (ADR-036):
 * `https:` (`http:` only when `VITE_APP_ENV=local`). Support links use the same gate. CSP `img-src`
 * allows `https:` so a logo does not depend on a build-time origin matching `VITE_ASSET_BASE_URL`.
 *
 * Rejection is silent by design: the caller falls back to the text wordmark or the bundled default.
 * A merchant's broken asset URL is not the player's problem and must not become a visible error.
 */
export function isSafeAssetUrl(
  value: unknown,
  options: {
    allowHttp?: boolean | undefined;
  } = {},
): value is string {
  const allowHttp = options.allowHttp ?? env.isLocal;

  if (typeof value !== 'string' || value.trim() === '') return false;

  // A protocol-relative `//evil.example/x` parses fine against a base and inherits our scheme, so it
  // must be rejected before parsing rather than after.
  if (value.startsWith('//')) return false;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    // Relative URLs land here and are rejected: every legitimate merchant asset is absolute.
    return false;
  }

  const schemeOk = url.protocol === 'https:' || (allowHttp && url.protocol === 'http:');
  return schemeOk;
}

/**
 * `mailto:` is only built from something that looks like an address. This is not address
 * verification — it is a check that the string cannot smuggle another scheme, a second recipient, or
 * a header into the link. Hence the strict domain and the permissive-but-bounded local part: a comma
 * or a newline reaching a `mailto:` is a header-injection vector.
 */
const EMAIL_RE =
  /^[^\s@,;:<>()[\]\\"]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;

export function isSafeEmail(value: unknown): value is string {
  return typeof value === 'string' && value.length <= 254 && EMAIL_RE.test(value);
}

const PHONE_RE = /^[+0-9\s()-]{5,20}$/;

export function isSafePhone(value: unknown): value is string {
  return typeof value === 'string' && PHONE_RE.test(value);
}
